import { useMemo, useState, useEffect } from 'react';
import { Loader } from '@g17eco/atoms/loader';
import { useGetSubsidiaryGroupsQuery } from '@api/subsidiary-groups';
import { InitiativePlain } from '@g17eco/types/initiative';
import { InitiativeGroup } from '@g17eco/types/initiative-group';
import { SelectFactory, SelectTypes } from './SelectFactory';
import { DropdownIndicator } from './DropdownIndicator';
import { Option } from './types';

interface Props {
  initiativeId: string;
  subsidiaries: InitiativePlain[];
  selectedSubsidiaryIds: string[];
  onChange: (subsidiaryIds: string[]) => void;
  placeholder?: string;
  isDisabled?: boolean;
}

const getGroupSubsidiaryIds = (group: InitiativeGroup, validIds: Set<string>) =>
  group.group.map((item) => item.initiativeId).filter((id) => validIds.has(id));

export const SubsidiaryGroupSelect = ({
  initiativeId,
  subsidiaries,
  selectedSubsidiaryIds,
  onChange,
  placeholder = 'Select subsidiaries',
  isDisabled = false,
}: Props) => {
  const { data: groups = [], isLoading } = useGetSubsidiaryGroupsQuery(initiativeId, { skip: !initiativeId });

  const validIds = useMemo(() => new Set(subsidiaries.map((s) => s._id)), [subsidiaries]);
  const groupIds = useMemo(() => new Set(groups.map((g) => g._id)), [groups]);
  const selectedIds = useMemo(
    () => selectedSubsidiaryIds.filter((id) => validIds.has(id)),
    [selectedSubsidiaryIds, validIds]
  );

  const [checkedGroupIds, setCheckedGroupIds] = useState<string[] | null>(null);

  // Initialize checked groups once when groups are loaded
  useEffect(() => {
    if (checkedGroupIds !== null || groups.length === 0) {
      return;
    }
    const initialChecked = groups
      .filter((group) => {
        const subsidiaryIds = getGroupSubsidiaryIds(group, validIds);
        return subsidiaryIds.length > 0 && subsidiaryIds.every((id) => selectedIds.includes(id));
      })
      .map((group) => group._id);
    setCheckedGroupIds(initialChecked);
  }, [checkedGroupIds, groups, validIds, selectedIds]);

  const checkedGroups = checkedGroupIds ?? [];

  const options: Option<string>[] = useMemo(() => {
    const groupOptions = groups.length === 0
      ? [{ value: 'no-groups', label: 'No groups available', isDisabled: true }]
      : groups.map((g) => ({ value: g._id, label: g.name, searchString: g.name }));

    const subsidiaryOptions = subsidiaries.map((s) => ({ value: s._id, label: s.name, searchString: s.name }));

    return [
      { value: '', label: 'Groups', isDisabled: true },
      ...groupOptions,
      { value: '', label: 'Subsidiaries', isDisabled: true },
      ...subsidiaryOptions,
    ];
  }, [groups, subsidiaries]);

  const values = useMemo(
    () => [...selectedIds, ...checkedGroups],
    [selectedIds, checkedGroups]
  );

  const handleChange = (newValues: string[]) => {
    const newCheckedGroups = newValues.filter((id) => groupIds.has(id));
    const newSelectedSubs = new Set(newValues.filter((id) => id && !newCheckedGroups.includes(id)));

    const addedGroups = newCheckedGroups.filter((id) => !checkedGroups.includes(id));
    const removedGroups = checkedGroups.filter((id) => !newCheckedGroups.includes(id));

    const getMemberIds = (groupId: string) => {
      const group = groups.find((g) => g._id === groupId);
      return group ? getGroupSubsidiaryIds(group, validIds) : [];
    };

    // Add subsidiaries of newly checked groups
    for (const groupId of addedGroups) {
      for (const id of getMemberIds(groupId)) {
        newSelectedSubs.add(id);
      }
    }

    // Remove subsidiaries of unchecked groups (keep if covered by other checked groups)
    const coveredIds = new Set(newCheckedGroups.flatMap(getMemberIds));
    for (const groupId of removedGroups) {
      for (const id of getMemberIds(groupId)) {
        if (!coveredIds.has(id)) {
          newSelectedSubs.delete(id);
        }
      }
    }

    // Auto-uncheck groups when all their members are deselected
    const updatedCheckedGroups = newCheckedGroups.filter((groupId) => {
      const memberIds = getMemberIds(groupId);
      return memberIds.some((id) => newSelectedSubs.has(id));
    });

    setCheckedGroupIds(updatedCheckedGroups);
    onChange([...newSelectedSubs]);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <SelectFactory
      selectType={SelectTypes.MultipleSelect}
      placeholder={placeholder}
      options={options}
      values={values}
      onChange={handleChange}
      isDisabled={isDisabled}
      isMenuPortalTargetBody
      menuPlacement='auto'
      components={{
        DropdownIndicator: () => (
          <DropdownIndicator
            current={selectedIds.length}
            limit={subsidiaries.length}
            showCount
            showDropdownIndicator
            indicateHasReachedLimit={false}
          />
        ),
      }}
    />
  );
};
